/* eslint-disable simple-import-sort/imports */
import { motion } from 'framer-motion';
import { styled } from 'styled-components';

export const HeroWrapper = styled.div`
  height: 350vh;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}) {
    height: 100%;
  }
`;

export const SectionHero = styled(motion.section)`
  position: sticky;
  top: 0;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.secondary};
  min-height: 100dvh;
  color: ${({ theme }) => theme.colors.defaultWhite};
  z-index: 1;
  padding-bottom: 45px;

  .invoice__container {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}) {
    height: 100%;
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 50px 0 60px 0;

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}) {
    padding: 20px 0 40px 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    padding: 38px 0 40px 0;
  }
`;

export const PowerIconStyled = styled(motion.svg)`
  width: auto;
  height: auto;
  color: ${({ theme }) => theme.colors.secondary};
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

export const EasyBrow = styled.div`
  --inset: 40px;
  --glow-hue: 180deg;

  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-family: 'Hanken Grotesk', sans-serif;
  border-radius: 37px;
  padding: 6px 14px;
  background-color: ${({ theme }) => theme.colors.defaultWhite};
  color: ${({ theme }) => theme.colors.secondary};
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 40px;
  position: relative;
  isolation: isolate;

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}) {
    margin-bottom: 36px;
  }
`;

export const EasyBrowText = styled(motion.span)`
  color: ${({ theme }) => theme.colors.secondary};
  position: relative;
  z-index: 1;
`;

export const Title = styled.h1`
  display: block;
  font-family: 'Hanken Grotesk', sans-serif;
  font-size: clamp(3.125rem, 6.25vw + 1.25rem, 6.875rem);
  font-style: normal;
  font-weight: 900;
  line-height: clamp(2.375rem, 3.117vw + 1.537rem, 3.875rem);
  text-align: center;
  margin-bottom: 50px;

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}) {
    font-size: 62px;
    margin-bottom: 27px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    font-size: 37px;
    margin-bottom: 20px;
  }
`;

export const Description = styled.span`
  font-family: 'Hanken Grotesk', sans-serif;
  font-size: clamp(16px, 0.8vw + 16px, 24px);
  font-weight: 400;
  margin-bottom: 34px;
  max-width: 610px;
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}) {
    font-size: 18px;
    margin-bottom: 36px;
  }
`;

export const CalendarIconStyled = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

export const LineIconStyled = styled.img`
  position: absolute;
  left: -54px;
  top: 35px;
  width: 45px;
  height: 6px;
  flex-shrink: 0;
  z-index: 10;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    display: none;
  }
`;

export const HeroButton = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 55px;
  padding: 16px 30px;
  background: rgb(68, 167, 253);
  color: ${({ theme }) => theme.colors.defaultWhite};
  text-decoration: none;
  font-family: 'Hanken Grotesk', sans-serif;
  font-weight: 600;
  transition: all ${({ theme }) => theme.constant.transition};
  white-space: nowrap;
  margin-bottom: 24px;
  box-shadow: 0 4px 15px rgba(68, 167, 253, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(68, 167, 253, 0.5);
    opacity: 1;
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 2px 10px rgba(68, 167, 253, 0.4);
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}) {
    margin-bottom: 43px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    margin-bottom: 38px;
  }
`;

export const ImageWrapper = styled(motion.div)`
  position: relative;
  border-radius: 18px;
  width: clamp(300px, 90vw, 700px);
  height: clamp(200px, 60vw, 474px);
  max-width: 100%;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid ${({ theme }) => theme.colors.defaultWhite};
  overflow: hidden;
  padding: 10px;
  margin: 0 auto;
  box-sizing: border-box;

  div {
    position: absolute;
    inset: 10px;
    width: calc(100% - 20px);
    height: calc(100% - 20px);
    overflow: hidden;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint?.desktop || '1024px'}) {
    width: 100%;
    max-width: 100%;
    height: clamp(
      268px,
      calc(268px + (660 - 268) * ((100vw - 480px) / (1024 - 480))),
      660px
    );
    padding: 8px;
    border-radius: 10px;
    margin-bottom: 28px;

    div {
      inset: 8px;
      width: calc(100% - 16px);
      height: calc(100% - 16px);
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoint?.tablet || '768px'}) {
    height: clamp(
      268px,
      calc(268px + (500 - 268) * ((100vw - 480px) / (768 - 480))),
      500px
    );
    border-radius: 10px;
    margin-bottom: 28px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint?.mobile || '480px'}) {
    height: clamp(258px, 60vw, 268px);
    padding: 6px;
    margin-bottom: 22px;

    div {
      inset: 6px;
      width: calc(100% - 12px);
      height: calc(100% - 12px);
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.smallMobile}) {
    height: clamp(258px, 60vw, 268px);
    margin-bottom: 22px;
  }
`;

export const ImageHero = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
`;

export const InfoWrapper = styled(motion.div)`
  position: relative;
  width: 100%;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;

  @media (max-width: ${({ theme }) => theme.breakpoint?.desktop}) {
    gap: 12px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    gap: 18px;
  }
`;

export const CardWrapper = styled(motion.div)`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: fit-content;
  min-height: 136px;

  @media (max-width: ${({ theme }) => theme.breakpoint.desktop}) {
    min-height: 117px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    min-height: 136px;
  }

  ${({ $position }) =>
    $position === 'left' &&
    `
    right: -60px;
    top: 260px;
  `}

  ${({ $position }) =>
    $position === 'center' &&
    `
    left: -64px;
    top: 134px;
  `}

  ${({ $position }) =>
    $position === 'right' &&
    `
    right: 4px;
    top: 50px;
  `}

    @media (max-width: ${({ theme }) => theme.breakpoint.desktop}) {
    position: relative;
    width: 100%;
    left: auto;
    right: auto;
    top: auto;
    bottom: auto;
  }
`;

export const CardContainer = styled.div`
  position: relative;
  width: 300px;
  height: 136px;

  @media (max-width: ${({ theme }) => theme.breakpoint.desktop}) {
    width: 100%;
    max-width: 100%;
    min-height: 117px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint?.mobile}) {
    min-height: 136px;
  }
`;

export const RecommendedTitle = styled.h3`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  font-weight: 700;
  font-size: 16px;
  line-height: 160%;
  text-align: center;
  padding: 20px 0;
  margin: 0;
  color: ${({ theme }) => theme.colors.gray600};
  width: 100%;
  z-index: 10;

  @media (max-width: ${({ theme }) => theme.breakpoint?.mobile}) {
    font-size: 14px;
    padding: 16px 0;
  }
`;
